import { z } from 'zod';
import { DEFAULT_PROTECTION, protectionSettingsSchema } from './protection.ts';
import { TEST_MAX_VISITS } from './synthetic.ts';

/**
 * Plans, credits and the admin area. 1 credit = 1 synthetic visit ("visits" in the interface). Every plan
 * gives a number of visits a month, renewed on the day the plan started; they do not accumulate. The
 * admin can add manual credits (support, a tailored Enterprise deal). A test reserves its visits when it
 * starts and gets back the ones that failed on our side, were blocked by anti-bot or never ran.
 *
 * Plans differ in four things only: visits a month, projects (one site each), how many own personas, members. Everything
 * else (missions, inspections, the persona configurator for one's own personas, reports) is the same for
 * everyone, with one limit of visits per test.
 */

export const ORG_PLANS = ['free', 'pro', 'enterprise', 'internal'] as const;
export type OrgPlan = (typeof ORG_PLANS)[number];

/** How plans are named in the interface. */
export const PLAN_LABEL: Record<OrgPlan, string> = { free: 'Free', pro: 'Pro', enterprise: 'Enterprise', internal: 'Internal' };

export const ORG_STATUSES = ['active', 'suspended'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const GRANT_KINDS = ['monthly', 'manual'] as const;
export type GrantKind = (typeof GRANT_KINDS)[number];

export const CREDIT_REASONS = ['grant', 'reserve', 'refund', 'revoke'] as const;
export type CreditReason = (typeof CREDIT_REASONS)[number];

const count = (max = 100_000) => z.number().int().min(0).max(max);

export const planLimitsSchema = z.object({
  /** Visits granted at the start of every monthly period of the plan; they expire at its end. */
  monthlyCredits: count(),
  /** Projects of the organization; each tests one site. */
  maxProjects: z.number().int().min(1).max(1000),
  /**
   * Personas of one's own (created, drafted from a sentence, or a ready-made one changed); null: no limit.
   * The ready-made personas, as they ship, are always there and never count.
   */
  maxOwnPersonas: z.number().int().min(0).max(1000).nullable(),
  /** Members of the organization (Clerk); null: no limit. */
  maxMembers: z.number().int().min(1).max(1000).nullable(),
  /**
   * Real visitors' sessions recorded by the script each monthly period (same period as the visits), across the
   * organization's projects; null: no limit. Beyond it new sessions are not recorded until the period renews;
   * sessions already started go on.
   */
  monthlySessions: z.number().int().min(0).max(100_000_000).nullable(),
});
export type PlanLimits = z.infer<typeof planLimitsSchema>;

export const appSettingsSchema = z.object({
  plans: z.object({
    free: planLimitsSchema,
    pro: planLimitsSchema,
    enterprise: planLimitsSchema,
    /** Our own organizations: never charged, so `monthlyCredits` is ignored. */
    internal: planLimitsSchema,
  }),
  /** The same for every plan. */
  limits: z.object({
    /** Visitors of one test. */
    maxVisitsPerTest: z.number().int().min(1).max(TEST_MAX_VISITS),
    /** Real visitors' recordings are kept at most this many days (a project can ask for fewer). */
    maxRetentionDays: z.number().int().min(1).max(3650),
  }),
  features: z.object({
    /** Tests started from the home page (the first test of a site, its re-runs). */
    freeAnalysis: z.boolean(),
  }),
  /** Protections against abuse: Free visits per IP, daily budget of the Free plan, runners, opted-out sites. */
  protection: protectionSettingsSchema,
});
export type AppSettings = z.infer<typeof appSettingsSchema>;

export const DEFAULT_SETTINGS: AppSettings = {
  plans: {
    free: { monthlyCredits: 10, maxProjects: 1, maxOwnPersonas: 1, maxMembers: 1, monthlySessions: 300 },
    pro: { monthlyCredits: 150, maxProjects: 3, maxOwnPersonas: null, maxMembers: 3, monthlySessions: 3000 },
    enterprise: { monthlyCredits: 600, maxProjects: 15, maxOwnPersonas: null, maxMembers: null, monthlySessions: 15_000 },
    internal: { monthlyCredits: 0, maxProjects: 100, maxOwnPersonas: null, maxMembers: null, monthlySessions: null },
  },
  limits: { maxVisitsPerTest: 50, maxRetentionDays: 30 },
  features: { freeAnalysis: true },
  protection: DEFAULT_PROTECTION,
};

/** The paid plans in order, for "Pro includes 3" next to a limit. */
const UPGRADE_ORDER: OrgPlan[] = ['free', 'pro', 'enterprise'];

/** The message when an organization has as many projects (sites) as its plan includes. */
export function projectsLimitMessage(plan: OrgPlan, plans: AppSettings['plans']): string {
  const max = plans[plan].maxProjects;
  const next = UPGRADE_ORDER[UPGRADE_ORDER.indexOf(plan) + 1];
  const words = (n: number) => `${n} ${n === 1 ? 'project' : 'projects'}`;
  const more = next && UPGRADE_ORDER.includes(plan) ? ` ${PLAN_LABEL[next]} includes ${plans[next].maxProjects}.` : ' Write to us if you need more.';
  return `Your plan includes ${words(max)}, one site each.${more}`;
}

export const updateOrgSchema = z
  .object({
    plan: z.enum(ORG_PLANS),
    status: z.enum(ORG_STATUSES),
    notes: z.string().max(2000),
    /** Members allowed for this organization only, whatever its plan; null: the plan's. */
    maxMembers: z.number().int().min(1).max(1000).nullable(),
  })
  .partial();
export type UpdateOrgInput = z.infer<typeof updateOrgSchema>;

/** Manual credits: support, or the extra visits of a tailored Enterprise plan. */
export const createGrantSchema = z.object({
  kind: z.literal('manual').default('manual'),
  amount: z.number().int().min(1).max(100_000),
  /** Omit for credits that never expire. */
  validDays: z.number().int().min(1).max(3650).optional(),
  note: z.string().trim().max(500).optional(),
});
export type CreateGrantInput = z.input<typeof createGrantSchema>;

export const updateUserSchema = z.object({ admin: z.boolean(), banned: z.boolean() }).partial();
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/**
 * Plan of one of the user's organizations, from the user list (plans belong to organizations: every
 * member gets it). The organization is created on our side if it never used the app yet.
 */
export const setUserPlanSchema = z.object({ externalOrgId: z.string().trim().min(1).max(200), plan: z.enum(ORG_PLANS) });
export type SetUserPlanInput = z.infer<typeof setUserPlanSchema>;

/** Effective limits of an organization: its plan's, plus the visits per test that apply to everyone. */
export interface OrgLimitsDto {
  maxProjects: number;
  monthlyCredits: number;
  /** Internal organizations are never charged. */
  unlimitedCredits: boolean;
  maxVisitsPerTest: number;
  /** Personas of one's own the plan includes; null: no limit. */
  maxOwnPersonas: number | null;
  maxMembers: number | null;
  /** Real visitors' sessions a month across the organization's projects; null: no limit. */
  monthlySessions: number | null;
  /** Days real visitors' recordings are kept at most. */
  maxRetentionDays: number;
}

/** Real visitors' sessions recorded in the current monthly period, across the organization's projects. */
export interface SessionUsageDto {
  used: number;
  /** Null: no limit. */
  limit: number | null;
  /** When the count starts again (the start of the next period). */
  renewsAt: string;
}

export interface MeDto {
  org: { id: string; name: string };
  authMode: 'clerk' | 'admin-token' | 'test';
  isAdmin: boolean;
  plan: OrgPlan;
  status: OrgStatus;
  credits: number;
  /** When this month's visits are renewed (the start of the next period); null for internal organizations. */
  creditsRenewAt: string | null;
  limits: OrgLimitsDto;
  sessions: SessionUsageDto;
  billing: BillingStateDto;
}

/** The organization's payments (Stripe), for Plan & billing. */
export interface BillingStateDto {
  /** Online payments are set up (Stripe keys): otherwise plans change by writing to us. */
  enabled: boolean;
  /** A live subscription (active, or a renewal being retried): the plan changes in the customer portal. */
  subscribed: boolean;
  /** Stripe's subscription status, if there ever was one. */
  status: string | null;
  /** A cancelled subscription keeps the plan until this date. */
  planEndsAt: string | null;
  /** The organization has paid online: invoices, payment method and plan in the customer portal. */
  canManage: boolean;
}

export interface CreditGrantDto {
  id: string;
  kind: GrantKind;
  amount: number;
  remaining: number;
  expiresAt: string | null;
  expired: boolean;
  revoked: boolean;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CreditTransactionDto {
  id: number;
  delta: number;
  reason: CreditReason;
  grantKind: GrantKind | null;
  testId: string | null;
  testName: string | null;
  note: string | null;
  actor: string | null;
  createdAt: string;
}

export interface AdminOrgSummaryDto {
  id: string;
  externalId: string;
  name: string;
  plan: OrgPlan;
  status: OrgStatus;
  credits: number;
  projects: number;
  tests: number;
  visits30d: number;
  costUsd30d: number;
  lastTestAt: string | null;
  createdAt: string;
}

export interface AdminMemberDto {
  userId: string;
  name: string;
  email: string | null;
  role: string;
}

export interface AdminProjectDto {
  id: string;
  name: string;
  siteUrl: string | null;
  tests: number;
  createdAt: string;
}

export interface AdminOrgDetailDto extends AdminOrgSummaryDto {
  notes: string;
  limits: OrgLimitsDto;
  grants: CreditGrantDto[];
  transactions: CreditTransactionDto[];
  projectList: AdminProjectDto[];
  recentTests: { id: string; projectId: string; name: string; status: string; visits: number; costUsd: number; createdAt: string }[];
  /** Null when the user directory (Clerk) is not configured. */
  members: AdminMemberDto[] | null;
  /** Members set for this organization by an admin; null: the plan's (`limits.maxMembers` is the one in effect). */
  customMaxMembers: number | null;
  /** The cap Clerk applies to invitations now (0: none); null when unknown or Clerk is not configured. */
  clerkMemberLimit: number | null;
  /** Most members Clerk allows per organization on our Clerk plan (20 without the B2B add-on); null: no cap. */
  clerkMemberCap: number | null;
  /** Set when the members limit could not be applied in Clerk just now. */
  memberLimitError?: string;
  /** Stripe: who pays and the subscription; null fields when the organization never paid online. */
  billing: { customerId: string | null; subscriptionId: string | null; status: string | null; planEndsAt: string | null; email: string | null };
}

export interface AdminUserDto {
  id: string;
  name: string;
  email: string | null;
  imageUrl: string | null;
  isAdmin: boolean;
  banned: boolean;
  createdAt: string;
  lastSignInAt: string | null;
  /** `orgId` and `plan` are null when the organization never used the app (no row on our side yet). */
  organizations: { externalId: string; orgId: string | null; name: string; role: string; plan: OrgPlan | null }[];
}

export interface AdminUserListDto {
  available: boolean;
  items: AdminUserDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminOverviewDto {
  orgs: number;
  orgsByPlan: Record<OrgPlan, number>;
  suspended: number;
  projects: number;
  tests7d: number;
  visits30d: number;
  costUsd30d: number;
  /** Tests started from the home page (first tests of a site and their re-runs). */
  freeAnalyses7d: number;
  creditsOutstanding: number;
  creditsUsed30d: number;
  creditsGranted30d: number;
  /** Projects whose script has sent at least one real session. */
  scriptsInstalled: number;
  /** Real visitors' sessions recorded in the last 30 days. */
  realSessions30d: number;
  /** Paying organizations (Pro, Enterprise), and those with the script on at least one project. */
  paying: number;
  payingWithScript: number;
  /**
   * Return within 14 days, split by the script: organizations whose first test is at least 14 days old, and how
   * many started a second test within 14 days of the first. "With script" = installed within those 14 days.
   */
  secondTest14d: { withScript: CohortDto; withoutScript: CohortDto };
  /** Invitations to the script in the last 30 days (`script_prompt_shown` / `_clicked`), by where they were. */
  scriptPrompts30d: { shown: ScriptPromptCountsDto; clicked: ScriptPromptCountsDto };
}

export interface CohortDto {
  orgs: number;
  returned: number;
}

export interface ScriptPromptCountsDto {
  report: number;
  emptyState: number;
}

export interface AdminMeDto {
  userId: string;
  label: string;
  /** The Clerk user directory is configured: users can be listed and managed. */
  directory: boolean;
  /** The admin's IP as the server sees it: what the daily limits count (to check the setup after a deploy). */
  clientIp: string | null;
  /** Only requests through Cloudflare are served, and the IP comes from CF-Connecting-IP. */
  cloudflareEdge: boolean;
}

export interface AdminSettingsDto {
  settings: AppSettings;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AdminAuditEntryDto {
  id: number;
  actor: string;
  action: string;
  target: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}
